import { Directive, HostBinding, HostListener, Input, OnDestroy } from '@angular/core';

const SKIP_INPUT_TYPES = new Set([
  'checkbox',
  'radio',
  'file',
  'hidden',
  'password',
  'date',
  'datetime-local',
  'time',
  'color',
  'range',
  'button',
  'submit',
  'reset',
]);

/**
 * Applies shared read-only styling to entity form zones (checker review).
 * Use with assets/sass/entity-form-zone-readonly.scss
 *
 * Also shows a hover tooltip when a field value is truncated.
 */
@Directive({
  selector: '[entityFormReadonly], [recipeFormReadonly]',
})
export class EntityFormReadonlyDirective implements OnDestroy {
  @Input() entityFormReadonly = false;
  /** @deprecated Use entityFormReadonly */
  @Input() recipeFormReadonly = false;

  private tooltipEl: HTMLDivElement | null = null;
  private measureCtx: CanvasRenderingContext2D | null = null;

  @HostBinding('class.entity-form-zone--readonly')
  @HostBinding('class.recipe-form-zone--readonly')
  get isReadOnly(): boolean {
    return this.entityFormReadonly || this.recipeFormReadonly;
  }

  @HostListener('mouseover', ['$event'])
  onMouseOver(event: MouseEvent): void {
    const field = this.resolveField(event.target as HTMLElement | null);
    const text = field ? this.getFieldText(field) : '';
    if (!field || !text || !this.isOverflowing(field, text)) {
      this.hideTooltip();
      return;
    }
    this.showTooltip(text, field);
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.hideTooltip();
  }

  ngOnDestroy(): void {
    if (this.tooltipEl) {
      this.tooltipEl.remove();
      this.tooltipEl = null;
    }
  }

  private resolveField(from: HTMLElement | null): HTMLElement | null {
    if (!from) {
      return null;
    }
    if (from.closest('.section-approval-toolbar, .workflow-remark-overlay, .ingredient-modal, .location-modal-overlay')) {
      return null;
    }

    const direct = from.closest('input, textarea, ng-select') as HTMLElement | null;
    if (direct && this.isTooltipField(direct)) {
      return direct;
    }

    const group = from.closest('.form-group, .form-group-sm, td, .sku-field, .meta-field, .ng-select');
    if (!group) {
      return null;
    }
    const nested = group.querySelector(
      'input.form-control, input.smart-input, textarea.form-control, textarea.smart-input, ng-select'
    ) as HTMLElement | null;
    return nested && this.isTooltipField(nested) ? nested : null;
  }

  private isTooltipField(el: HTMLElement): boolean {
    if (el.tagName === 'NG-SELECT') {
      return true;
    }
    if (!(el instanceof HTMLInputElement) && !(el instanceof HTMLTextAreaElement)) {
      return false;
    }
    return el instanceof HTMLTextAreaElement || !SKIP_INPUT_TYPES.has((el.type || 'text').toLowerCase());
  }

  private getFieldText(field: HTMLElement): string {
    if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) {
      return (field.value || '').trim();
    }
    const label = field.querySelector('.ng-value-label, .ng-value') as HTMLElement | null;
    return (label?.textContent || '').trim();
  }

  private isOverflowing(field: HTMLElement, text: string): boolean {
    if (field instanceof HTMLInputElement) {
      return field.scrollWidth > field.clientWidth + 2 || this.inputValueLikelyClipped(field);
    }
    if (field instanceof HTMLTextAreaElement) {
      return field.scrollHeight > field.clientHeight + 2 || field.scrollWidth > field.clientWidth + 2;
    }
    const label = field.querySelector('.ng-value-label, .ng-value') as HTMLElement | null;
    if (label && label.scrollWidth > label.clientWidth + 2) {
      return true;
    }
    const container = field.querySelector('.ng-value-container, .ng-select-container') as HTMLElement | null;
    if (container && container.scrollWidth > container.clientWidth + 2) {
      return true;
    }
    return text.length > 42;
  }

  private inputValueLikelyClipped(input: HTMLInputElement): boolean {
    const value = input.value || '';
    if (value.length < 16) {
      return false;
    }
    const style = window.getComputedStyle(input);
    const ctx = this.getMeasureContext();
    if (!ctx) {
      return value.length > 36;
    }
    ctx.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    const padding = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
    return ctx.measureText(value).width > input.clientWidth - padding - 2;
  }

  private getMeasureContext(): CanvasRenderingContext2D | null {
    if (!this.measureCtx) {
      this.measureCtx = document.createElement('canvas').getContext('2d');
    }
    return this.measureCtx;
  }

  private showTooltip(text: string, field: HTMLElement): void {
    const tip = this.ensureTooltip();
    tip.textContent = text;
    tip.hidden = false;

    const rect = field.getBoundingClientRect();
    const tipRect = tip.getBoundingClientRect();
    const gap = 8;
    let top = rect.bottom + gap;
    if (top + tipRect.height > window.innerHeight - 8) {
      top = Math.max(8, rect.top - tipRect.height - gap);
    }
    let left = rect.left;
    if (left + tipRect.width > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - tipRect.width - 8);
    }
    tip.style.top = `${Math.round(top)}px`;
    tip.style.left = `${Math.round(left)}px`;
  }

  private hideTooltip(): void {
    if (this.tooltipEl) {
      this.tooltipEl.hidden = true;
    }
  }

  private ensureTooltip(): HTMLDivElement {
    if (!this.tooltipEl) {
      this.tooltipEl = document.createElement('div');
      this.tooltipEl.className = 'entity-field-overflow-tooltip';
      this.tooltipEl.setAttribute('role', 'tooltip');
      document.body.appendChild(this.tooltipEl);
    }
    return this.tooltipEl;
  }
}

